import { useContext, useEffect, useState } from 'react';
import { Alert, Badge, Button, Checkbox, Divider, Group, Modal, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { httpsCallable } from 'firebase/functions';
import { notifications } from '@mantine/notifications';
import { MyUserDataContext } from '../contexts';
import { useFunctions } from '../helpers/firebaseContext';
import type { BaseResult, CreateCampaignRequest, CustomClaims, Fund, NavCampaignConfig } from '../types';
import { navCampaignPayload, renderNavCampaignPayload, validateNavCampaignConfig } from '../../../shared/navCampaign';
import BasicCampaignForm from '../routes/campaigns/basic_campaign_form';
import { RmsSegmentSelectComponent } from './rms_segment_select_component';

export default function FundNavCampaignSettings({ fund }: { fund: Fund }) {
    const user = useContext(MyUserDataContext);
    const claims = user.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined;
    const functions = useFunctions();
    const [opened, setOpened] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState<NavCampaignConfig['status']>('inactive');
    const [sendAutomatically, setSendAutomatically] = useState(false);
    const [savedConfig, setSavedConfig] = useState<NavCampaignConfig>();
    useEffect(() => setSavedConfig(undefined), [fund.navCampaign]);
    const form = useForm<CreateCampaignRequest>({ initialValues: {
        title: '{{fund.name_en}} - Details as of {{fund.nav_date}}', type: ['sms'],
        smsMessage: '{{fund.name_ar}} في {{fund.nav_date}}\nصافي قيمة الوحدة (NAV): {{fund.nav_per_unit}}' +
            (typeof fund.weekly_annualized_return === 'number' ? '\nالعائد الأسبوعي المحتسب سنوياً: {{fund.weekly_annualized_return}}%' : ''),
        customerRecipients: [], tagsRecipients: fund.id ? [fund.id] : [], segmentsRecipients: [],
        rmRecipients: [], rmRecipientRoles: ['rm', 'operations'], messageVariables: {}, updateLink: { updateType: 'broadcast' },
    } });
    if (!claims?.admin && !claims?.oper) return null;

    const config: NavCampaignConfig = { status, sendAutomatically, payload: navCampaignPayload(form.values) };
    let preview = '';
    try { preview = renderNavCampaignPayload(config, fund).smsMessage || ''; } catch { /* Save shows validation errors. */ }

    return <>
        <Button size="xs" variant="light" onClick={() => {
            const existing = savedConfig || fund.navCampaign;
            if (existing) {
                form.setValues(existing.payload);
                setStatus(existing.status);
                setSendAutomatically(existing.sendAutomatically);
            } else {
                form.reset();
                setStatus('inactive');
                setSendAutomatically(false);
            }
            setError('');
            setOpened(true);
        }}>NAV campaign{(savedConfig || fund.navCampaign)?.status === 'active' ? ' · Active' : ''}</Button>
        <Modal opened={opened} onClose={() => !saving && setOpened(false)} title={`NAV campaign · ${fund.name_en}`} size="xl">
            <form onSubmit={form.onSubmit(async () => {
                setError('');
                try {
                    validateNavCampaignConfig(config);
                    setSaving(true);
                    await httpsCallable<{ fundID: string; config: NavCampaignConfig }, BaseResult>(functions, 'saveFundNavCampaign')({ fundID: fund.id, config });
                    setSavedConfig(config);
                    setOpened(false);
                    notifications.show({ color: 'green', message: 'NAV campaign settings saved.' });
                } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
                finally { setSaving(false); }
            })}>
                <Stack>
                    <Text size="sm">Each NAV or NAV-date change creates a campaign using these saved settings. Active settings are required.</Text>
                    <Select label="Campaign status" data={['active', 'inactive']} value={status} allowDeselect={false}
                        onChange={value => setStatus(value as NavCampaignConfig['status'])} />
                    <Switch label="Send automatically" checked={sendAutomatically} onChange={event => setSendAutomatically(event.currentTarget.checked)}
                        description="Run the campaign automatically after creation. When off, create it for review and use Run Campaign to send it." />
                    <TextInput label="Campaign title" required {...form.getInputProps('title')} />
                    <Text size="sm">Recipients: clients subscribed to this fund, selected automatically using the fund’s tag. RM recipients below receive additional copies.</Text>
                    <Divider />
                    <Text size="sm">Use {'{{fund.name_en}}'}, {'{{fund.name_ar}}'}, {'{{fund.nav_date}}'}, {'{{fund.nav_per_unit}}'} or {'{{fund.weekly_annualized_return}}'} in messages and message variables. Values are filled when the NAV changes.</Text>
                    <BasicCampaignForm form={form} funds={[]} showNotifyRm={false} preserveTemplates />
                    {preview && <><Badge variant="light">SMS preview · current NAV</Badge><Text dir="auto" style={{ whiteSpace: 'pre-wrap' }}>{preview}</Text></>}
                    <Divider />
                    <Checkbox.Group label="RM recipient roles" value={form.values.rmRecipientRoles || []}
                        onChange={value => form.setFieldValue('rmRecipientRoles', value as ('rm' | 'operations')[])}>
                        <Group mt="xs"><Checkbox value="rm" label="RM" /><Checkbox value="operations" label="Operations" /></Group>
                    </Checkbox.Group>
                    <RmsSegmentSelectComponent textProps={{ label: 'RM segments' }} segmentsList={form.values.rmRecipients}
                        onChange={value => form.setFieldValue('rmRecipients', value)} />
                    {error && <Alert color="red">{error}</Alert>}
                    <Group justify="flex-end"><Button variant="default" disabled={saving} onClick={() => setOpened(false)}>Cancel</Button><Button type="submit" loading={saving}>Save settings</Button></Group>
                </Stack>
            </form>
        </Modal>
    </>;
}
